import { movies, type CategoryKey, type Movie } from '@/mocks/movies';

export interface CategoryMeta {
  key: CategoryKey;
  slug: CategoryKey;
  label: string;
  enLabel: string;
  icon: string;
  intro: string;
  description: string;
}

export const categoryList: CategoryMeta[] = [
  {
    key: 'movie',
    slug: 'movie',
    label: '电影',
    enLabel: 'MOVIES',
    icon: 'ri-film-line',
    intro: '从院线新片到口碑佳作，按类型与年份找到今晚想看的那一部。',
    description:
      '黄牛视频电影频道收录院线新片、口碑佳作与经典修复作品，提供影片详情、演职员信息与类型筛选，帮助你快速找到想看的电影。',
  },
  {
    key: 'tv',
    slug: 'tv',
    label: '电视剧',
    enLabel: 'SERIES',
    icon: 'ri-tv-2-line',
    intro: '古装、悬疑、都市、青春，追更中的剧集与完结好剧都在这里。',
    description:
      '黄牛视频电视剧频道覆盖古装、悬疑、都市、青春、家庭等类型，提供剧集简介、集数信息与相关推荐，方便追剧与补剧。',
  },
  {
    key: 'variety',
    slug: 'variety',
    label: '综艺',
    enLabel: 'VARIETY',
    icon: 'ri-mic-line',
    intro: '音乐、推理、喜剧、旅行，每周更新的下饭综艺清单。',
    description:
      '黄牛视频综艺频道汇集音乐竞演、推理探案、喜剧脱口秀与生活纪实类节目，提供节目介绍与更新信息。',
  },
];

export const categoryMap: Record<CategoryKey, CategoryMeta> = {
  movie: categoryList[0],
  tv: categoryList[1],
  variety: categoryList[2],
};

export function getCategory(slug?: string): CategoryMeta | undefined {
  if (!slug) return undefined;
  return categoryList.find((item) => item.key === slug);
}

export const sortOptions = [
  { key: 'updated', label: '按更新时间' },
  { key: 'heat', label: '按热度' },
  { key: 'rating', label: '按评分' },
] as const;

export type SortKey = (typeof sortOptions)[number]['key'];

export function inCategory(category: CategoryKey): Movie[] {
  return movies.filter((movie) => movie.category === category);
}

export function genresOf(list: Movie[]): string[] {
  const set = new Set<string>();
  list.forEach((movie) => movie.genres.forEach((genre) => set.add(genre)));
  return Array.from(set);
}

export function regionsOf(list: Movie[]): string[] {
  return Array.from(new Set(list.map((movie) => movie.region)));
}

export function yearsOf(list: Movie[]): string[] {
  return Array.from(new Set(list.map((movie) => String(movie.year)))).sort(
    (a, b) => Number(b) - Number(a),
  );
}

export interface FilterParams {
  genres: string[];
  regions: string[];
  years: string[];
  sort: SortKey;
}

export function filterMovies(list: Movie[], params: FilterParams): Movie[] {
  const { genres, regions, years, sort } = params;
  const result = list.filter((movie) => {
    const genreOk =
      genres.length === 0 || movie.genres.some((genre) => genres.includes(genre));
    const regionOk = regions.length === 0 || regions.includes(movie.region);
    const yearOk = years.length === 0 || years.includes(String(movie.year));
    return genreOk && regionOk && yearOk;
  });

  return [...result].sort((a, b) => {
    if (sort === 'heat') return b.heat - a.heat;
    if (sort === 'rating') return b.rating - a.rating;
    return b.updatedAt.localeCompare(a.updatedAt);
  });
}

export function getMovieById(id?: string): Movie | undefined {
  if (!id) return undefined;
  return movies.find((movie) => movie.id === id);
}

export function getRecommendations(movie: Movie, count = 6): Movie[] {
  const sameCategory = movies.filter(
    (item) => item.id !== movie.id && item.category === movie.category,
  );
  const scored = sameCategory
    .map((item) => {
      const overlap = item.genres.filter((genre) => movie.genres.includes(genre)).length;
      const sameRegion = item.region === movie.region ? 1 : 0;
      return { item, score: overlap * 10 + sameRegion * 3 + item.rating };
    })
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, count).map((entry) => entry.item);
}

export function searchMovies(keyword: string): Movie[] {
  const query = keyword.trim().toLowerCase();
  if (!query) return [];
  return movies.filter((movie) => {
    const haystack = [
      movie.title,
      movie.director,
      movie.region,
      ...movie.genres,
      ...movie.cast,
      movie.tagline,
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(query) || movie.title.toLowerCase().includes(query);
  });
}

export function suggestMovies(keyword: string, count = 6): Movie[] {
  const query = keyword.trim();
  if (!query) return [];
  return searchMovies(query).slice(0, count);
}

export function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.getFullYear()} 年 ${date.getMonth() + 1} 月 ${date.getDate()} 日`;
}

export function newUpdated(): Movie[] {
  return [...movies].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function hotMovies(count = 8): Movie[] {
  return [...movies].sort((a, b) => b.heat - a.heat).slice(0, count);
}

export function featuredMovies(): Movie[] {
  return movies.filter((movie) => movie.featured);
}